import type { ImagePickerAsset } from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';

import { UserFacingError } from '@/src/utils/errors';

export const MAX_PHOTO_UPLOAD_SOURCE_BYTES = 200 * 1024 * 1024;
export const JPEG_CONTENT_TYPE = 'image/jpeg';

export type PreparedImageUpload = {
  blob: Blob;
  contentType: string;
  fileName: string;
  height: number;
  sizeBytes: number;
  uri: string;
  usedOriginal: boolean;
  width: number;
};

type PrepareImageUploadInput = {
  asset: ImagePickerAsset;
  fallbackFileName: string;
  jpegQuality: number;
  maxBytes?: number;
  manipulatorActions?: ImageManipulator.Action[];
};

const extensionContentTypes: Record<string, string> = {
  avif: 'image/avif',
  heic: 'image/heic',
  heif: 'image/heif',
  jfif: 'image/jpeg',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

const contentTypeExtensions: Record<string, string> = {
  'image/avif': 'avif',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

function cleanContentType(value: unknown) {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.split(';')[0]?.trim().toLowerCase() ?? '';

  if (!normalized) {
    return null;
  }

  return normalized === 'image/jpg' ? JPEG_CONTENT_TYPE : normalized;
}

function contentTypeFromPath(value: unknown) {
  if (typeof value !== 'string') {
    return null;
  }

  const cleanValue = value.split('?')[0]?.split('#')[0] ?? '';
  const extension = cleanValue.match(/\.([a-zA-Z0-9]+)$/)?.[1]?.toLowerCase();

  return extension ? (extensionContentTypes[extension] ?? null) : null;
}

function imageContentTypeExtension(contentType: string) {
  return contentTypeExtensions[contentType] ?? 'img';
}

function normalizeDimension(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.round(value)
    : 0;
}

function sanitizeFileBase(value: string) {
  const withoutQuery = value.split('?')[0]?.split('#')[0] ?? value;
  const lastPathSegment = withoutQuery.split('/').filter(Boolean).pop() ?? value;
  const withoutExtension = lastPathSegment.replace(/\.[^.]+$/, '');
  const sanitized = withoutExtension.replace(/[^a-zA-Z0-9._-]/g, '_');

  return sanitized || 'photo';
}

export function inferImageContentType(asset: ImagePickerAsset, blob?: Blob) {
  return (
    cleanContentType(asset.mimeType) ??
    cleanContentType(blob?.type) ??
    contentTypeFromPath(asset.fileName) ??
    contentTypeFromPath(asset.uri) ??
    JPEG_CONTENT_TYPE
  );
}

export function isHeicImageContentType(contentType: string) {
  return contentType === 'image/heic' || contentType === 'image/heif';
}

export function assetNeedsCompatibleImagePreview(asset: ImagePickerAsset) {
  return isHeicImageContentType(inferImageContentType(asset));
}

export function safeImageUploadFileName({
  asset,
  contentType,
  fallbackFileName,
}: {
  asset: ImagePickerAsset;
  contentType: string;
  fallbackFileName: string;
}) {
  const base = sanitizeFileBase(asset.fileName ?? fallbackFileName);
  const extension = imageContentTypeExtension(contentType);

  return `${base}.${extension}`;
}

export function validatePreparedImageSize(
  sizeBytes: number,
  maxBytes = MAX_PHOTO_UPLOAD_SOURCE_BYTES,
) {
  if (sizeBytes <= 0) {
    throw new UserFacingError(
      'We could not read that photo. Please choose a different image.',
    );
  }

  if (sizeBytes > maxBytes) {
    throw new UserFacingError(
      'Photos must be 200MB or smaller after processing.',
    );
  }
}

function blobFromURIWithXHR(uri: string) {
  if (typeof XMLHttpRequest === 'undefined') {
    return null;
  }

  return new Promise<Blob>((resolve, reject) => {
    const request = new XMLHttpRequest();

    request.onload = () => {
      resolve(request.response as Blob);
    };
    request.onerror = () => {
      reject(new Error('Unable to read selected media file.'));
    };
    request.responseType = 'blob';
    request.open('GET', uri, true);
    request.send(null);
  });
}

export async function blobFromURI(uri: string) {
  let fetchError: unknown = null;

  try {
    const response = await fetch(uri);
    const blob = await response.blob();

    if (blob.size > 0 || !uri.startsWith('file://')) {
      return blob;
    }
  } catch (error) {
    fetchError = error;
  }

  const xhrBlob = uri.startsWith('file://') ? blobFromURIWithXHR(uri) : null;

  if (xhrBlob) {
    return xhrBlob;
  }

  throw fetchError ?? new Error('Unable to read selected media file.');
}

function blobFromWebFile(asset: ImagePickerAsset) {
  const file = asset.file;

  if (!file || typeof file !== 'object') {
    return null;
  }

  if (typeof file.size !== 'number' || typeof file.type !== 'string') {
    return null;
  }

  return file;
}

async function originalBlobFromAsset(asset: ImagePickerAsset) {
  try {
    return await blobFromURI(asset.uri);
  } catch {
    const fileBlob = blobFromWebFile(asset);

    if (fileBlob) {
      return fileBlob;
    }

    throw new UserFacingError(
      'That photo format could not be read on this device. Please try a different photo.',
    );
  }
}

export async function blobFromMediaAsset(
  asset: ImagePickerAsset,
  errorMessage: string,
) {
  try {
    const blob = await blobFromURI(asset.uri);

    if (blob.size > 0) {
      return blob;
    }

    const fileBlob = blobFromWebFile(asset);

    return fileBlob ?? blob;
  } catch {
    const fileBlob = blobFromWebFile(asset);

    if (fileBlob) {
      return fileBlob;
    }

    throw new UserFacingError(errorMessage);
  }
}

export async function prepareImageUpload({
  asset,
  fallbackFileName,
  jpegQuality,
  maxBytes = MAX_PHOTO_UPLOAD_SOURCE_BYTES,
  manipulatorActions = [],
}: PrepareImageUploadInput): Promise<PreparedImageUpload> {
  const selectedContentType = inferImageContentType(asset);

  if (!selectedContentType.startsWith('image/')) {
    throw new UserFacingError('Selected file must be a photo.');
  }

  let manipulationError: unknown = null;

  try {
    const optimized = await ImageManipulator.manipulateAsync(
      asset.uri,
      manipulatorActions,
      {
        compress: jpegQuality,
        format: ImageManipulator.SaveFormat.JPEG,
      },
    );
    const blob = await blobFromURI(optimized.uri);
    const sizeBytes = blob.size;

    validatePreparedImageSize(sizeBytes, maxBytes);

    return {
      blob,
      contentType: JPEG_CONTENT_TYPE,
      fileName: safeImageUploadFileName({
        asset: { ...asset, fileName: fallbackFileName },
        contentType: JPEG_CONTENT_TYPE,
        fallbackFileName,
      }),
      height: normalizeDimension(optimized.height),
      sizeBytes,
      uri: optimized.uri,
      usedOriginal: false,
      width: normalizeDimension(optimized.width),
    };
  } catch (error) {
    manipulationError = error;
  }

  const originalBlob = await originalBlobFromAsset(asset);

  const originalContentType = inferImageContentType(asset, originalBlob);

  if (!originalContentType.startsWith('image/')) {
    throw new UserFacingError('Selected file must be a photo.');
  }

  const originalSizeBytes = originalBlob.size || asset.fileSize || 0;
  validatePreparedImageSize(originalSizeBytes, maxBytes);

  if (!originalBlob.size && manipulationError) {
    throw new UserFacingError(
      'That photo format could not be prepared. Please try a different photo.',
    );
  }

  return {
    blob: originalBlob,
    contentType: originalContentType,
    fileName: safeImageUploadFileName({
      asset,
      contentType: originalContentType,
      fallbackFileName,
    }),
    height: normalizeDimension(asset.height),
    sizeBytes: originalSizeBytes,
    uri: asset.uri,
    usedOriginal: true,
    width: normalizeDimension(asset.width),
  };
}
